import SlideToggle from 'react-slide-toggle';
import {useSearchParams} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
import StickyBox from 'react-sticky-box';
import Tree from 'rc-tree';

import {connect} from "react-redux";
import {getProductList, setCategoryFilter, setProductAttributeFilter, setProductBrandFilter, setProductPage, setProductPriceFilter} from "../../../store/products/actions";
import {updateSearchParams} from "../../../utils";
import useTranslation from "../../../utils/hooks/useTranslation";

function findCategoryNode( nodes, id ) {
    if ( !nodes || id == null ) return null;
    for ( const node of nodes ) {
        if ( node.id === id ) return node;
        const found = findCategoryNode( node.children, id );
        if ( found ) return found;
    }
    return null;
}

function findCategoryParent( nodes, id, parent = null ) {
    if ( !nodes || id == null ) return null;
    for ( const node of nodes ) {
        if ( node.id === id ) return parent;
        const found = findCategoryParent( node.children, id, node );
        if ( found ) return found;
    }
    return null;
}

// Resolve the attribute filters for a category. If the selected category has
// no attributes of its own, walk up the parent tree and use the attributes of
// the nearest ancestor that defines them.
function findCategoryAttributes( nodes, id ) {
    let currentId = id;
    while ( currentId != null ) {
        const node = findCategoryNode( nodes, currentId );
        if ( !node ) return [];
        if ( Array.isArray( node.attributes ) && node.attributes.length ) {
            return node.attributes;
        }
        const parent = findCategoryParent( nodes, currentId );
        currentId = parent ? parent.id : null;
    }
    return [];
}

function ShopSidebar ({display, right, category, setCategoryFilter, selectedCategory, setProductPage, minPrice, maxPrice, setProductPriceFilter, attributeOptionIds, setProductAttributeFilter, brands, selectedBrand, setProductBrandFilter} ) {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {list, loading, error} = category;
    const selectedChildrenCategory = getChildrenOfChildrenCategoryParentId(selectedCategory);
    const selectedParentCategory = getChildrenCategoryParentId( selectedChildrenCategory ? selectedChildrenCategory : selectedCategory);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [minPriceInput, setMinPriceInput] = useState(minPrice ?? '');
    const [maxPriceInput, setMaxPriceInput] = useState(maxPrice ?? '');

    useEffect(() => {
        setMinPriceInput(minPrice ?? '');
        setMaxPriceInput(maxPrice ?? '');
    }, [minPrice, maxPrice]);
    // Show only the selected category as the root node, with its children
    // nested beneath it — never the full category list. When nothing is
    // selected, show nothing.
    const displayList = useMemo( () => {
        if ( !list ) return [];
        if ( selectedCategory == null ) return list;

        const selectedNode = findCategoryNode( list, selectedCategory );
        return selectedNode ? [ selectedNode ] : list;
    }, [ list, selectedCategory ] );

    const categories = useMemo( () => {
        return ( displayList || [] ).reduce( ( acc, cur ) => {
            acc.push( {
                key: cur.id,
                title: <b>{cur.name}</b>,
                children: fixChildrenFormat( cur.children )
            } );

            return acc;
        }, [] );
    }, [ displayList ] );

    // Attributes are attached to the selected category node. When a category
    // with attributes is selected we render them as additional filters below
    // the category tree.
    const attributes = useMemo( () => {
        if ( selectedCategory == null ) return [];
        return findCategoryAttributes( list, selectedCategory );
    }, [ list, selectedCategory ] );

    const selectedAttributeOptions = Array.isArray( attributeOptionIds ) ? attributeOptionIds : [];
    const hasActiveAttributes = selectedAttributeOptions.length > 0;

    useEffect( () => {
        return () => {
            closeSidebar();
        }
    }, [] )

    useEffect(() => {
        setExpandedKeys([selectedChildrenCategory, selectedParentCategory, selectedCategory]);
    }, [list, selectedCategory, selectedChildrenCategory, selectedParentCategory]);

    useEffect(() => {
        let treeNodes = document.querySelectorAll('.rc-tree-treenode');
        let isChildren = false;
        treeNodes = [...treeNodes].filter((node, index) => {
            if(node.classList.contains('rc-tree-treenode-switcher-open')) {
                isChildren = true;
            }

            if(isChildren) {
                node.classList.add("border-0");
            }

            if(node.classList.contains('rc-tree-treenode-leaf-last') && index < (treeNodes.length - 1)) {
                isChildren = false;
            }

            return node.classList.contains('rc-tree-treenode-switcher-close')
        })
    }, [expandedKeys])

    function fixChildrenFormat(children) {
        let childNodes = [];
        children?.forEach((child) => {
            childNodes.push({
                key: child.id,
                title: <b>{child.name}</b>,
                children: fixChildrenFormat(child.children)
            });
        })

        return childNodes;
    }

    function filterByCategory ( selected ) {
        let categoryId = selected[0];

        if(!categoryId) {
            return;
        }

        setCategoryFilter(categoryId);

        let params = {
            category: categoryId,
        };

        setProductPage(1);

        params = updateSearchParams(searchParams, params);
        // Attribute filters are reset when the category changes, so drop the
        // stale param from the URL too.
        delete params.attribute_option_id;
        setSearchParams(params);
    }

    function clearAllCategories() {
        setCategoryFilter(null);
        setProductPage(1);
        setSearchParams({page: 1});
    }

    function clearCategory() {
        // Go up one level: select the parent of the current category instead of
        // jumping straight back to the full list. Only when the category is
        // already top-level (no parent) do we clear the selection entirely.
        const parent = findCategoryParent(list, selectedCategory);
        const nextCategory = parent ? parent.id : null;

        setCategoryFilter(nextCategory);
        setProductPage(1);

        const params = {};
        searchParams.forEach((value, key) => {
            // Clearing the category also clears its attribute filters.
            if (key !== 'category' && key !== 'attribute_option_id') params[key] = value;
        });
        if (nextCategory != null) params.category = nextCategory;
        params.page = 1;
        setSearchParams(params);
    }

    function applyPriceFilter(e) {
        e?.preventDefault?.();

        const minRaw = minPriceInput === '' || minPriceInput === null ? null : Number(minPriceInput);
        const maxRaw = maxPriceInput === '' || maxPriceInput === null ? null : Number(maxPriceInput);

        let min = Number.isFinite(minRaw) && minRaw >= 0 ? minRaw : null;
        let max = Number.isFinite(maxRaw) && maxRaw >= 0 ? maxRaw : null;

        if (min !== null && max !== null && min > max) {
            [min, max] = [max, min];
        }

        setProductPriceFilter({ min, max });
        setProductPage(1);

        const baseParams = {};
        searchParams.forEach((value, key) => {
            if (key !== 'min_price' && key !== 'max_price') baseParams[key] = value;
        });
        if (min !== null) baseParams.min_price = String(min);
        if (max !== null) baseParams.max_price = String(max);
        baseParams.page = 1;
        setSearchParams(baseParams);
    }

    function clearPriceFilter() {
        setProductPriceFilter({ min: null, max: null });
        setMinPriceInput('');
        setMaxPriceInput('');
        setProductPage(1);

        const params = {};
        searchParams.forEach((value, key) => {
            if (key !== 'min_price' && key !== 'max_price') params[key] = value;
        });
        params.page = 1;
        setSearchParams(params);
    }

    const hasActivePriceFilter = minPrice !== null || maxPrice !== null;

    function applyAttributeFilter( nextOptionIds ) {
        setProductAttributeFilter( nextOptionIds );
        setProductPage( 1 );

        const params = {};
        searchParams.forEach( ( value, key ) => {
            if ( key !== 'attribute_option_id' ) params[ key ] = value;
        } );
        if ( nextOptionIds.length ) params.attribute_option_id = nextOptionIds.join( ',' );
        params.page = 1;
        setSearchParams( params );
    }

    function toggleAttributeOption( attribute, optionId ) {
        const current = selectedAttributeOptions;
        const isSelected = current.includes( optionId );

        let next;
        if ( isSelected ) {
            next = current.filter( id => id !== optionId );
        } else if ( attribute.allow_multiple ) {
            next = [ ...current, optionId ];
        } else {
            // Single-select attribute: replace any previously chosen option of
            // the same attribute with the new one.
            const siblingIds = ( attribute.options || [] ).map( o => o.id );
            next = current.filter( id => !siblingIds.includes( id ) );
            next.push( optionId );
        }

        applyAttributeFilter( next );
    }

    function clearAttributes() {
        applyAttributeFilter( [] );
    }

    function applyBrandFilter( next ) {
        setProductBrandFilter( next );
        setProductPage( 1 );

        const params = {};
        searchParams.forEach( ( value, key ) => {
            if ( key !== 'brand_id' ) params[ key ] = value;
        } );
        if ( next != null ) params.brand_id = next;
        params.page = 1;
        setSearchParams( params );
    }

    function onBrandChange( e ) {
        const value = e?.target?.value;
        applyBrandFilter( value ? Number( value ) : null );
    }

    function findCategoryName(nodes, id) {
        if(!nodes || id == null) return null;
        for (const n of nodes) {
            if (n.id === id) return n.name;
            const found = findCategoryName(n.children, id);
            if (found) return found;
        }
        return null;
    }

    const activeCategoryName = findCategoryName(list, selectedCategory);

    function toggleCategory( categoryId ) {
        expandedKeys.includes(categoryId) ? setExpandedKeys(expandedKeys.filter(i => i !== categoryId)) : setExpandedKeys([ ...expandedKeys, categoryId ]);
    }

    function getChildrenCategoryParentId(categoryId) {
        return list?.filter(categories => categories.children.some(cat => cat.id === categoryId))[0]?.id;
    }

    function getChildrenOfChildrenCategoryParentId(categoryId) {
        return list?.flatMap(categories => {
            let res = categories.children.filter(cat => cat.children.some(childCat => childCat.id === categoryId))[0]?.id;
            return res ? res : [];
        })[0];
    }

    function closeSidebar () {
        document.querySelector( 'body' ).classList.contains( 'sidebar-opened' ) && document.querySelector( 'body' ).classList.remove( 'sidebar-opened' );
    }

    if ( error ) {
        return <div>{ error.message }</div>
    }

    return (
        <>
            <div className="sidebar-overlay" onClick={ closeSidebar }></div>
            <aside className={ `sidebar-shop col-lg-3  mobile-sidebar skeleton-body skel-shop-products ${ !loading ? 'loaded' : '' } ${ display === 'none' ? 'd-lg-none' : '' } ${ right ? '' : 'order-lg-first' }` }>
                <StickyBox className="sidebar-wrapper" offsetTop={ 70 }>
                    {/*<div className="widget widget-price">*/}
                    {/*    <h3 className="widget-title">*/}
                    {/*        <a>{t("label.price") || "Price"}</a>*/}
                    {/*        {hasActivePriceFilter ? (*/}
                    {/*            <button type="button" className="sidebar-clear" onClick={clearPriceFilter}>*/}
                    {/*                {t("label.clear") || "Clear"}*/}
                    {/*            </button>*/}
                    {/*        ) : null}*/}
                    {/*    </h3>*/}
                    {/*    <div className="widget-body">*/}
                    {/*        {hasActivePriceFilter ? (*/}
                    {/*            <span className="sidebar-active-chip mb-2">*/}
                    {/*                {minPrice !== null ? minPrice : '…'} – {maxPrice !== null ? maxPrice : '…'}*/}
                    {/*                <button type="button" onClick={clearPriceFilter} aria-label="Clear">&times;</button>*/}
                    {/*            </span>*/}
                    {/*        ) : null}*/}
                    {/*        <form className="price-range-form" onSubmit={applyPriceFilter}>*/}
                    {/*            <div className="price-range-inputs">*/}
                    {/*                <input*/}
                    {/*                    type="number"*/}
                    {/*                    min="0"*/}
                    {/*                    inputMode="numeric"*/}
                    {/*                    className="form-control price-input"*/}
                    {/*                    placeholder={t("label.min") || "Min"}*/}
                    {/*                    value={minPriceInput}*/}
                    {/*                    onChange={(e) => setMinPriceInput(e.target.value)}*/}
                    {/*                />*/}
                    {/*                <span className="price-range-dash">–</span>*/}
                    {/*                <input*/}
                    {/*                    type="number"*/}
                    {/*                    min="0"*/}
                    {/*                    inputMode="numeric"*/}
                    {/*                    className="form-control price-input"*/}
                    {/*                    placeholder={t("label.max") || "Max"}*/}
                    {/*                    value={maxPriceInput}*/}
                    {/*                    onChange={(e) => setMaxPriceInput(e.target.value)}*/}
                    {/*                />*/}
                    {/*            </div>*/}
                    {/*            <button type="submit" className="btn btn-dark btn-block price-apply-btn">*/}
                    {/*                {t("label.apply") || "Apply"}*/}
                    {/*            </button>*/}
                    {/*        </form>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    <div className="widget">
                        <SlideToggle>
                            { ( { setCollapsibleElement} ) => (
                                <>
                                    <h3 className="widget-title">
                                        <a>{t("label.categories")}</a>
                                        {selectedCategory ? (
                                            <button type="button" className="sidebar-clear" onClick={clearAllCategories}>
                                                {t("label.clear") || "Clear"}
                                            </button>
                                        ) : null}
                                    </h3>
                                    <div className="overflow-hidden" ref={ setCollapsibleElement }>
                                        <div className="widget-body">
                                            {activeCategoryName ? (
                                                <span className="sidebar-active-chip m-2">
                                                    {activeCategoryName}
                                                    <button type="button" onClick={clearCategory} aria-label="Clear">&times;</button>
                                                </span>
                                            ) : null}
                                            <Tree
                                                className={`no-icon cat-list border-0`}
                                                selectable={ true }
                                                showIcon={ false }
                                                expandedKeys={ expandedKeys }
                                                switcherIcon={ ( props ) => {
                                                    return ( !props.isLeaf ?
                                                        <span className="toggle" onClick={() => toggleCategory(props.eventKey)}></span>
                                                        : ''
                                                    )
                                                } }
                                                selectedKeys={ [ selectedCategory ] }
                                                treeData={ categories }
                                                onSelect={ filterByCategory }
                                            />
                                        </div>
                                    </div>
                                </>
                            ) }
                        </SlideToggle>
                    </div>

                    {attributes.length ? (
                        <div className="widget widget-attributes">
                            <h3 className="widget-title">
                                <a>{t("label.filters")}</a>
                                {hasActiveAttributes ? (
                                    <button type="button" className="sidebar-clear" onClick={clearAttributes}>
                                        {t("label.clear") || "Clear"}
                                    </button>
                                ) : null}
                            </h3>
                            <div className="widget-body px-3 py-2">
                                {attributes.map((attribute) => (
                                    <div className="attribute-group" key={attribute.id}>
                                        <span className="attribute-group__title">{attribute.name}</span>
                                        <ul className="attribute-options">
                                            {(attribute.options || []).map((option) => {
                                                const active = selectedAttributeOptions.includes(option.id);
                                                return (
                                                    <li key={option.id}>
                                                        <button
                                                            type="button"
                                                            className={`attribute-option${active ? ' active' : ''}`}
                                                            aria-pressed={active}
                                                            onClick={() => toggleAttributeOption(attribute, option.id)}
                                                        >
                                                            {option.value}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {brands && brands.length ? (
                        <div className="widget widget-brands">
                            <h3 className="widget-title">
                                <a>{t("label.brand")}</a>
                            </h3>
                            <div className="widget-body px-3 py-2">
                                <div className="select-custom">
                                    <select
                                        className="form-control"
                                        value={selectedBrand ?? ''}
                                        onChange={onBrandChange}
                                    >
                                        <option value="">{t("label.all_brands") || "All brands"}</option>
                                        {brands.map((brand) => (
                                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </StickyBox>
            </aside>
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        category: state.categories,
        selectedCategory: state.products.selected_category,
        minPrice: state.products.min_price,
        maxPrice: state.products.max_price,
        attributeOptionIds: state.products.attribute_option_ids,
        brands: state.partners.list,
        selectedBrand: state.products.selected_brand,
    }
}

const mapDispatchToProps = {setCategoryFilter, getProductList, setProductPage, setProductPriceFilter, setProductAttributeFilter, setProductBrandFilter}

export default connect( mapStateToProps, mapDispatchToProps )( ShopSidebar );
